import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { AtLeast } from 'common/types/Atleast';
import styles from './BudgetSectionLine.module.scss';
import utilsStyles from '../../Utils.module.scss';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { FaAngleDown, FaAngleRight, FaCubes } from 'react-icons/fa';
import Button from 'common/components/button/Button';
import { getOrderedLineColumns, isColumnVisible, LinesTableColumn } from '../../utils';
import BudgetLineColumn from '../../column/BudgetLineColumn';
import CustomTotalCell from '../../customTotalCell/CustomTotalCell';
import customTotalStyles from '../../customTotalCell/CustomTotalCell.module.scss';
import InlineTextInput from 'common/components/inlineTextInput/InlineTextInput';
import InlineNumberInput from 'common/components/inlineNumberInput/InlineNumberInput';
import InlineDropdownInput from 'common/components/inlineDropdownInput/InlineDropdownInput';
import { useTranslation } from 'react-i18next';
import { forwardRef, Ref, useContext, useMemo } from 'react';
import { LinesDataProvider } from '../../LinesDataProvider';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { BODY_TOOLTIP, DEFAULT_DECIMAL_PLACES } from 'Config';
import MathUtils from 'common/services/MathUtils';
import budgetElementLineStyles from '../element/BudgetElementLine.module.scss'
const MAX_LEVEL_TO_LIGHTEN = 3;

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
    level: number;
    isActive: boolean;
    onChange: (line: AtLeast<BudgetLineDto, 'key'>, calculateTotals: boolean) => void;
    childCount: number;
}

function BudgetSectionLine({ line, disabled, isActive, level, onChange ,childCount }: Props, ref: Ref<HTMLDivElement>): JSX.Element | null {
    const { t } = useTranslation();
    const { hiddenColumns, columnsOrder, units, isComponentEditor, rootSectionDesignationOverride } = useContext(LinesDataProvider);
    const orderedColumns = useMemo(() => getOrderedLineColumns(columnsOrder), [columnsOrder]);
    const unitsOptions = useMemo(
        () => units.map(u => ({ selectedLabel: u.symbol, label: u.name, value: u.id })),
        [units]
    );
    const isLockedRootSection = (isComponentEditor ?? false) && level === 0;
    const showLineNumber = isColumnVisible(hiddenColumns, LinesTableColumn.LINE_NUMBER);
    const displayDesignation = isLockedRootSection && rootSectionDesignationOverride !== undefined
        ? rootSectionDesignationOverride
        : line.designation;
    const decimalPlaces = useSelector<Reducers, number>(x => x.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);

    const effectiveGrossUnitPrice = useMemo(() => (
        (line.quantity ?? 0) > 0 ? MathUtils.divide(line.aggregateGrossAmount ?? 0, line.quantity!) : 0
    ), [line.aggregateGrossAmount, line.quantity]);

    const totalCostUnitPrice = useMemo(() => (
        (line.quantity ?? 0) > 0 ? MathUtils.divide(line.aggregateCostBaseAmount ?? 0, line.quantity!) : 0
    ), [line.aggregateCostBaseAmount, line.quantity]);

    const sectionUnitPrice = useMemo(() => (
        (line.quantity ?? 0) > 0 ? MathUtils.divide(line.totalExcludingTax, line.quantity!) : 0
    ), [line.totalExcludingTax, line.quantity]);

    const netProfit = useMemo(() => MathUtils.multiply(
        MathUtils.minus(sectionUnitPrice, totalCostUnitPrice),
        line.quantity ?? 0
    ), [sectionUnitPrice, totalCostUnitPrice, line.quantity]);

    const netProfitPercentage = useMemo(() => (
        line.totalExcludingTax !== 0
            ? MathUtils.multiply(MathUtils.divide(netProfit, line.totalExcludingTax), 100)
            : 0
    ), [netProfit, line.totalExcludingTax]);

    const renderMoney = (value: number, suffix?: string) => {
        if (line.quantity == null && value === 0) {
            return <span>-</span>;
        }
        return <MoneyFormat value={value} decimalScale={decimalPlaces} suffix={suffix} />;
    };

    const onChangeValue = (newLine: Partial<BudgetLineDto>) => {
        onChange({ key: line.key, ...newLine }, false);
    }

    const onToggleOpen = () => {
        onChange({ ...line, isOpen: !line.isOpen }, false);
    }

    const cellClass = (extra: string) => `${utilsStyles.column} ${styles.column} ${extra}`;

    const renderCell = (col: LinesTableColumn): JSX.Element | null => {
        switch (col) {
            case LinesTableColumn.DESIGNATION:
                return (
                    <BudgetLineColumn key={col} column={col} className={`${utilsStyles.column} ${styles.column} ${utilsStyles.columnAlignLeft} ${utilsStyles.designationColumn} ${styles.designationColumn}`}>
                        <Button size='normal' onClick={onToggleOpen} className={styles.toggleOpenButton}>
                            {!line.isOpen && <FaAngleRight />}
                            {line.isOpen && <FaAngleDown />}
                        </Button>
                        {showLineNumber && (
                            <div className={styles.number}>
                                <InlineTextInput
                                    value={isLockedRootSection ? '' : line.number}
                                    onBlur={v => {
                                        const newValue = v ?? '';
                                        if (newValue !== line.number) {
                                            onChangeValue({ numberOverride: newValue });
                                        }
                                    }}
                                    placeholder=""
                                    disabled={disabled}
                                />
                            </div>
                        )}
                        {line.sourceBudgetComponentId && (
                            <span
                                className={styles.componentBadge}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('budgets.budget.lines.component_linked')}
                            >
                                <FaCubes />
                            </span>
                        )}
                        {isLockedRootSection
                            ? (
                                <div className={styles.lockedDesignation}>
                                    {displayDesignation || t('budgets.budget.lines.without_designation')}
                                </div>
                            )
                            : (
                                <InlineTextInput
                                    value={displayDesignation}
                                    onBlur={v => onChangeValue({ designation: v ?? '' })}
                                    placeholder={t('budgets.budget.lines.without_designation')}
                                    disabled={disabled}
                                />
                            )}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.QUANTITY:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.quantityColumn)}>
                        <InlineNumberInput
                            value={line.quantity}
                            onBlur={v => onChange({ key: line.key, quantity: v }, true)}
                            className={'text-center'}
                            disabled={disabled}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.UNIT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.unityColumn)}>
                        <InlineDropdownInput
                            value={line.unitId}
                            options={unitsOptions}
                            onChange={v => {
                                const unit = units.find(u => u.id === v);
                                onChange({ key: line.key, unitId: v, unitSymbol: unit?.symbol ?? null }, true);
                            }}
                            className={'text-center'}
                            disabled={disabled}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.GROSS_UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.grossUnityColumn)}>
                        {renderMoney(effectiveGrossUnitPrice)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.DISCOUNT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.discountColumn)}>
                        {renderMoney(line.aggregateDiscountAmount ?? 0)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.AGGREGATED_ELEMENTS:
                return <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.aggregatedElementsColumn)} />;
            case LinesTableColumn.TOTAL_COST_UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalCostUnitPriceColumn)}>
                        {renderMoney(totalCostUnitPrice)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.MARGIN:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.marginColumn)}>
                        {renderMoney(line.aggregateMarginAmount ?? 0)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.DECENNIAL_INSURANCE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.decennialInsuranceColumn)}>
                        {renderMoney(line.aggregateInsuranceAmount ?? 0)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.pricePerUnityColumn)}>
                        {renderMoney(sectionUnitPrice)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TOTAL_EXCLUDING_TAX:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalWithoutTaxColumn)}>
                        <CustomTotalCell
                            total={line.totalExcludingTax}
                            customTotalText={line.customTotalText}
                            customTotalTextUserName={line.customTotalTextUserName}
                            decimalPlaces={decimalPlaces}
                            disabled={disabled}
                            onChangeCustomText={v => onChange({ key: line.key, customTotalText: v }, true)}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TAX:
                return <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.taxColumn)} />;
            case LinesTableColumn.TOTAL_INCLUDING_TAX:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalWithTaxColumn)}>
                        {line.customTotalText
                            ? <span className={customTotalStyles.mutedTotal}>—</span>
                            : renderMoney(line.totalIncludingTax)
                        }
                    </BudgetLineColumn>
                );
            case LinesTableColumn.NET_PROFIT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.netProfitColumn)}>
                        {renderMoney(netProfit)}
                    </BudgetLineColumn>
                );
            case LinesTableColumn.NET_PROFIT_PERCENTAGE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.netProfitPercentageColumn)}>
                        {renderMoney(netProfitPercentage, ' %')}
                    </BudgetLineColumn>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div
                className={styles.container}
                ref={ref}
            >
                <div
                    className={`${styles.section} ${level >= MAX_LEVEL_TO_LIGHTEN ? styles['level-' + MAX_LEVEL_TO_LIGHTEN] : styles['level-' + level]}`}
                >
                    {renderCell(LinesTableColumn.DESIGNATION)}
                    {orderedColumns.map(col => renderCell(col))}
                </div>
            </div>
            { line.isOpen && childCount === 0 && <div className={`${budgetElementLineStyles.container}`}>
                <div className={`${budgetElementLineStyles.element} ${styles.addLinesDiv}`}>
                    {t('budgets.budget.lines.add_lines_message')}
                </div>
            </div>}
        </div>

    );
}

export default forwardRef<HTMLDivElement, Props>(BudgetSectionLine);
